// Tenant-agnostic OAuth authorization entry (Agent Platform Phase 6).
//
// The RFC 8414 discovery document advertises ONE authorization_endpoint
// (/oauth/authorize), but the consent UI lives at a tenant-prefixed route.
// This page bridges the two: an authenticated user is forwarded to their own
// tenant's consent screen with the OAuth params intact; an unauthenticated
// user is sent to login first, with the authorize URL parked in
// sessionStorage so the sign-in flow can resume it (see MultiTenantSignIn).
import React, { useEffect, useState } from 'react';
import { Navigate, useLocation } from 'react-router-dom';
import { fetchSession } from '@/lib/session';
import { getCurrentTenant } from '@/lib/hrApi';

export const PENDING_AUTHORIZE_KEY = 'pendingOAuthAuthorize';

/** The signed-in tenant slug, read from non-credential UI state. Login flows
 * write different key sets (MultiTenantSignIn: currentTenantSlug; authService:
 * user_data/tenant_data) — accept any of them. The access token itself now lives
 * in an unreadable httpOnly cookie, so it is no longer part of this check. */
export function storedTenantSlug(): string | null {
  let slug = localStorage.getItem('currentTenantSlug') || localStorage.getItem('currentWorkspace');
  if (!slug) {
    try {
      const t = JSON.parse(localStorage.getItem('tenant_data') || 'null');
      const u = JSON.parse(localStorage.getItem('user_data') || localStorage.getItem('user') || 'null');
      slug = t?.slug || u?.tenantSlug || u?.tenant?.slug || null;
    } catch {
      slug = null;
    }
  }
  return slug;
}

/** Back-compat shim: legacy callers expect `{ token, slug }`. The credential now
 * lives in the httpOnly cookie (JS cannot read it), so `token` is always null;
 * authentication is confirmed via the async gateway session probe instead. */
export function storedSession(): { token: string | null; slug: string | null } {
  return { token: null, slug: storedTenantSlug() };
}

export const OAuthAuthorizePage: React.FC = () => {
  const { search } = useLocation();
  // Auth is now confirmed by the httpOnly session cookie via the gateway, so the
  // decision is async: 'checking' until the session probe resolves.
  const [state, setState] = useState<{ status: 'checking' | 'authed' | 'anon'; slug: string | null }>({
    status: 'checking',
    slug: null,
  });

  useEffect(() => {
    let active = true;
    (async () => {
      const user = await fetchSession();
      if (!active) return;
      let slug = storedTenantSlug();
      // A valid session with no locally-stored slug (fresh browser profile,
      // cleared storage, cookie-only session) used to bounce back to /login in
      // a loop — the OpenAI/Anthropic connector reviewers hit exactly that.
      // The session token knows the tenant, so resolve the slug server-side.
      if (user && !slug) {
        try {
          slug = (await getCurrentTenant())?.slug || null;
          if (slug) localStorage.setItem('currentTenantSlug', slug);
        } catch {
          slug = null;
        }
      }
      if (!active) return;
      setState(user && slug ? { status: 'authed', slug } : { status: 'anon', slug: null });
    })();
    return () => {
      active = false;
    };
  }, []);

  if (state.status === 'checking') return null;

  if (state.status === 'authed' && state.slug) {
    sessionStorage.removeItem(PENDING_AUTHORIZE_KEY);
    return <Navigate to={`/t/${state.slug}/app/oauth/consent${search}`} replace />;
  }

  // Park the full authorize URL for post-login continuation, then sign in.
  try {
    sessionStorage.setItem(PENDING_AUTHORIZE_KEY, `/oauth/authorize${search}`);
  } catch {
    // storage unavailable — login still works; the client will retry authorize
  }
  // Pass a workspace hint through to the sign-in page when the authorize URL
  // carries one (?workspace=… — used by connection guides and reviewer links),
  // so the reviewer lands directly on the email+password step.
  const ws = new URLSearchParams(search).get('workspace');
  return <Navigate to={ws ? `/login?workspace=${encodeURIComponent(ws)}` : '/login'} replace />;
};

export default OAuthAuthorizePage;
